import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { useApp } from '@/components/AppLayout';
import { useUpgradeModal } from '@/contexts/UpgradeModalContext';
import { businessApi, uploadApi, type IndustryChoice, type CurrencyChoice } from '@/lib/api';
import { Building, X } from 'lucide-react';

export function AddBusiness() {
  const navigate = useNavigate();
  const { currentBusiness, refreshUser } = useApp();
  const { showUpgradeModal } = useUpgradeModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [industries, setIndustries] = useState<IndustryChoice[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyChoice[]>([]);
  const [businessName, setBusinessName] = useState('');
  const [address, setAddress] = useState('');
  const [addressCoords, setAddressCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [industry, setIndustry] = useState('');
  const [industryOther, setIndustryOther] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    businessApi.getIndustries().then((response) => {
      if (response.data) {
        setIndustries(response.data);
      }
    });
    businessApi.getCurrencies().then((response) => {
      if (response.data) {
        setCurrencies(response.data);
      }
    });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }

    setError('');
    setLogoFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    let logoUrl = '';
    if (logoFile) {
      setUploadingLogo(true);
      const uploadResponse = await uploadApi.uploadLogo(logoFile);
      setUploadingLogo(false);

      if (uploadResponse.error) {
        setError(uploadResponse.error);
        setLoading(false);
        return;
      }

      logoUrl = uploadResponse.data?.url || '';
    }

    const response = await businessApi.create({
      name: businessName,
      address,
      address_latitude: addressCoords?.latitude,
      address_longitude: addressCoords?.longitude,
      industry,
      industry_other: industry === 'other' ? industryOther : '',
      logo: logoUrl,
      default_currency: defaultCurrency,
    });

    setLoading(false);

    if (response.limitExceeded) {
      showUpgradeModal(response.limitExceeded);
      return;
    }

    if (response.error) {
      setError(response.error);
      return;
    }

    await refreshUser();

    if (response.data) {
      navigate(`/${response.data.id}/dashboard`);
    }
  };

  const isFormValid = businessName && address;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b flex-shrink-0">
        <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
        <div className="h-6 w-px bg-border" />
        <span className="font-medium text-sm">{currentBusiness?.name}</span>
        <div className="flex-1" />
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col items-center px-4 pt-12 pb-16">
          <div className="w-full max-w-md">
            <h1 className="text-2xl font-semibold mb-2 flex items-center gap-2">
              Add New Business
              <Building className="h-5 w-5 text-primary" />
            </h1>
            <p className="text-muted-foreground mb-8">
              Create another business to manage separately.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="logo">
                  Business Logo <span className="text-muted-foreground font-normal">(Optional)</span>
                </Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="logo"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {logoPreview ? (
                  <div className="border-2 border-primary/50 rounded-lg p-4 flex items-center gap-4">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{logoFile?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {logoFile && (logoFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveLogo}
                      className="text-destructive hover:text-destructive"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center hover:border-muted-foreground/50 transition-colors cursor-pointer"
                  >
                    <div className="text-muted-foreground text-sm">
                      <svg
                        className="mx-auto h-8 w-8 mb-2 text-muted-foreground/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p>Click to upload your logo</p>
                      <p className="text-xs mt-1 text-muted-foreground/70">
                        JPEG, PNG, GIF, or WebP (max 5MB)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessName">Business Name</Label>
                <Input
                  id="businessName"
                  placeholder="Acme Services LLC"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Business Address</Label>
                <AddressAutocomplete
                  id="address"
                  placeholder="Start typing your business address..."
                  value={address}
                  onChange={(value, coords) => {
                    setAddress(value);
                    setAddressCoords(coords || null);
                  }}
                  rows={2}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">
                  Industry <span className="text-muted-foreground font-normal">(Optional)</span>
                </Label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {industry === 'other' && (
                <div className="space-y-2">
                  <Label htmlFor="industryOther">Please specify your industry</Label>
                  <Input
                    id="industryOther"
                    placeholder="Enter your industry"
                    value={industryOther}
                    onChange={(e) => setIndustryOther(e.target.value)}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="currency">
                  Default Currency <span className="text-muted-foreground font-normal">(Optional)</span>
                </Label>
                <Select value={defaultCurrency} onValueChange={setDefaultCurrency}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem key={currency.value} value={currency.value}>
                        {currency.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={loading || !isFormValid}>
                  {uploadingLogo
                    ? 'Uploading logo...'
                    : loading
                      ? 'Creating...'
                      : 'Create Business'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
